import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { getUserMe } from '../../../apps/main/rootReducer';
import Flex from '../../../components/Flex';
import StatBox from '../../../components/StatBox';
import { rounded } from '../../dashboard/components/util';
import { fetchEmployeeOverview } from '../../hr/api';
import AbsenceInsightsChart from './absenceOverViewChart/AbsenceInsightsChart';
import { makePrioStyles } from '../../../theme/utils';
import { fetchAbsencesMe } from '../actions';

const useStyles = makePrioStyles((theme) => ({
  root: {
    paddingTop: theme.spacing.small,
    display: 'flex',
    flexDirection: 'row',
    paddingBottom: theme.spacing.small,
    gap: theme.spacing.large,
    alignItems: 'center',
    paddingLeft: theme.spacing.regular,
    paddingRight: theme.spacing.regular,
    marginBottom: theme.spacing.large,
    backgroundColor: theme.colors.application.background.light,
    '& .prio-bar-chart-divider-text': {
      backgroundColor: theme.colors.application.background.light,
      boxShadow: 'none',
    },
  },
  statBox: {
    backgroundColor: theme.colors.application.background.selected,
  },
}));

interface AbsenceProposalFormInformationPanelProps {
  employeeId: string;
}

export const AbsenceProposalFormInformationPanel: React.FC<
  AbsenceProposalFormInformationPanelProps
> = (props) => {
  //#region ------------------------------ Defaults
  const { employeeId } = props;
  const { t } = useTranslation();
  const classes = useStyles();
  const userMe = useSelector(getUserMe);

  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors

  const { data: employeeAbsenceData } = useQuery({
    queryKey: ['employeeAbsenceData', employeeId],
    queryFn: async () => {
      const searchString = `EmployeeId eq '${employeeId}'`;
      const { data } = await fetchEmployeeOverview(searchString);
      if (data?.items?.length > 0) {
        return data.items[0];
      } else {
        console.warn('No valid data returned from fetchEmployeeOverview');
        return null;
      }
    },
    staleTime: 60000,
  });

  //#endregion

  //#region ------------------------------ Methods / Handlers
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    dispatch(fetchAbsencesMe());
  }, [dispatch]);
  //#endregion

  return (
    <div className={classes.root}>
      {userMe.id === employeeId && (
        <Flex.Item flex={3}>
          <AbsenceInsightsChart />
        </Flex.Item>
      )}

      <Flex.Item flex={1}>
        <StatBox
          title={t('absences:form.unsettledOvertimeHours')}
          value={`${rounded(employeeAbsenceData?.calculated?.unsettledOvertimeHours ?? 0)} h`}
          className={classes.statBox}
        />
      </Flex.Item>
    </div>
  );
};

export default AbsenceProposalFormInformationPanel;
